import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShieldCheck, Loader2, CreditCard } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { api, errMsg } from "../../lib/api";
import { rupiah } from "../../lib/format";

function loadSnap(url, clientKey) {
  return new Promise((resolve, reject) => {
    if (window.snap) return resolve();
    const s = document.createElement("script");
    s.src = url; s.setAttribute("data-client-key", clientKey);
    s.onload = resolve; s.onerror = reject;
    document.body.appendChild(s);
  });
}

export default function PayButton({ order, onPaid }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [mock, setMock] = useState(null);

  const refresh = () => { qc.invalidateQueries({ queryKey: ["order", order.order_id] }); qc.invalidateQueries({ queryKey: ["orders"] }); onPaid?.(); };

  const pay = async () => {
    setBusy(true);
    try {
      const { data: cfg } = await api.get("/payments/config");
      const { data } = await api.post(`/payments/${order.order_id}/token`);
      if (data.mock) { setMock(data); return; }
      await loadSnap(cfg.snap_url, cfg.client_key);
      window.snap.pay(data.token, {
        onSuccess: () => { toast.success("Pembayaran berhasil, menunggu konfirmasi Midtrans."); setTimeout(refresh, 2500); },
        onPending: () => { toast.info("Pembayaran menunggu penyelesaian."); refresh(); },
        onError: () => toast.error("Pembayaran gagal."),
        onClose: () => toast.info("Jendela pembayaran ditutup."),
      });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const confirmMock = async () => {
    setBusy(true);
    try {
      await api.post(`/payments/${order.order_id}/mock-pay`);
      toast.success("Pembayaran berhasil! Dana ditahan aman (escrow).");
      setMock(null); refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <Button onClick={pay} disabled={busy} data-testid="pay-btn" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl h-11 shadow-lg shadow-blue-600/25">
        {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <CreditCard size={16} className="mr-2" />} Bayar {rupiah(order.price)}
      </Button>
      <Dialog open={!!mock} onOpenChange={(o) => !o && setMock(null)}>
        <DialogContent className="sm:max-w-md" data-testid="mock-payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-extrabold">Simulasi Pembayaran Midtrans</DialogTitle>
            <DialogDescription>Kunci Midtrans belum diisi, jadi pembayaran disimulasikan. Alur escrow tetap berjalan seperti aslinya.</DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border border-slate-200 p-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Order ID</span><span className="font-bold text-slate-800">{order.order_id}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Layanan</span><span className="font-semibold text-slate-800">Inspeksi {order.vehicle_type_label}</span></div>
            <div className="flex justify-between border-t border-slate-100 pt-2"><span className="text-slate-500">Total</span><span className="font-extrabold text-blue-600 text-base">{rupiah(mock?.amount)}</span></div>
          </div>
          <div className="flex items-start gap-2 text-[11px] text-slate-500 bg-blue-50 rounded-lg p-3"><ShieldCheck size={16} className="text-blue-600 shrink-0" /> Dana ditahan platform dan baru diteruskan ke inspector setelah Anda menerima laporan.</div>
          <Button onClick={confirmMock} disabled={busy} data-testid="mock-pay-confirm-btn" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl h-11">
            {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : null} Konfirmasi Bayar (Simulasi)
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
